import { fmtDate } from "../../ForBrands/brandDealUtils";

// The game stats sheet: the pure model behind entering one game of a CACHE
// Partner team (Jayson, 10/3: per-game stats for the scholastic games
// Cerebro does not capture, Sierra Canyon first). Admin Team Management uses
// it today; a coach page can use it later. Plain data in, plain data out
// (tested in src/tests/gameStatsModel.test.js).
//
// A game is the game itself (date, opponent, home or away, where, the
// competition, the final score, draft or final) and one box score line per
// roster player, in the columns of Jayson's sheet. FG%, 3P% and FT% are
// worked out from the made and attempted shots, never typed.
//
// The server (backend/users/team_games.py and the games views) is the
// authority. The rules and messages here mirror its validation word for
// word, so the sheet can point at a cell before anything is sent, and its
// answers ({error, message, fields, lines}) land on the same cells.

// A value the sheet has nothing for (an en dash, never an em dash).
export const EMPTY = "–";

export const STATUS_DRAFT = "draft";
export const STATUS_FINAL = "final";
export const STATUSES = [STATUS_DRAFT, STATUS_FINAL];
export const HOME_AWAY = ["home", "away", "neutral"];
export const POSITIONS = ["G", "F", "C"];

// line.cerebro.status when Cerebro already has the game for that player:
// the line is kept but never counted twice.
export const COVERED = "covered";

// The earliest game date the server takes (the 2015-16 season).
export const FIRST_DATE = "2015-08-01";

// The box score in the sheet's order. `min` may carry one decimal (24.5);
// the rest are whole numbers.
export const STAT_KEYS = ["min", "pts", "fgm", "fga", "tpm", "tpa", "ftm", "fta", "reb", "ast", "stl", "blk", "tov", "pf"];

const STAT_MAX = { min: 70, pts: 150 };
export const statMax = (key) => STAT_MAX[key] ?? 100;

// Field lengths the server keeps.
export const MAX_OPPONENT = 80;
export const MAX_COMPETITION = 80;
export const MAX_CITY = 60;
export const MAX_STATE = 30;
export const MAX_JERSEY = 10;
export const MAX_POSITION = 20;
export const MAX_SCORE = 300;

const column = (key, label, name, kind, extra = {}) => ({ key, label, name, kind, ...extra });

// The sheet's columns, in the order of Jayson's sheet (Name, Jersey #,
// Position, MINUTES, PTS, FGM, FGA, FG%, 3PM, 3PA, 3FG%, FTM, FTA, FT%, REB,
// AST, STL, BLK, TO, PF). `name` is how a screen reader says the column.
export const COLUMNS = [
  column("player", "Player", "player", "player"),
  column("jersey", "#", "jersey number", "jersey"),
  column("position", "Pos", "position", "position"),
  column("min", "MIN", "minutes", "stat"),
  column("pts", "PTS", "points", "stat"),
  column("fgm", "FGM", "field goals made", "stat"),
  column("fga", "FGA", "field goals attempted", "stat"),
  column("fgPct", "FG%", "field goal percentage", "derived", { made: "fgm", att: "fga" }),
  column("tpm", "3PM", "3-pointers made", "stat"),
  column("tpa", "3PA", "3-pointers attempted", "stat"),
  column("tpPct", "3P%", "3-point percentage", "derived", { made: "tpm", att: "tpa" }),
  column("ftm", "FTM", "free throws made", "stat"),
  column("fta", "FTA", "free throws attempted", "stat"),
  column("ftPct", "FT%", "free throw percentage", "derived", { made: "ftm", att: "fta" }),
  column("reb", "REB", "rebounds", "stat"),
  column("ast", "AST", "assists", "stat"),
  column("stl", "STL", "steals", "stat"),
  column("blk", "BLK", "blocks", "stat"),
  column("tov", "TO", "turnovers", "stat"),
  column("pf", "PF", "personal fouls", "stat"),
];
export const COLUMN = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
export const DERIVED = COLUMNS.filter((c) => c.kind === "derived");

// "Minutes", "Field goals made": a column's name as a header tooltip.
export const columnTitle = (col) => (col ? col.name.charAt(0).toUpperCase() + col.name.slice(1) : "");

// ------------------------------------------------------------- Messages
// The server's words (backend games views), kept identical.
export const MESSAGES = {
  fix: "Fix the highlighted cells.",
  date: "Enter the game date.",
  opponent: "Enter the opponent.",
  homeAway: "Choose home, away or neutral.",
  location: `Enter a city of up to ${MAX_CITY} characters and a state of up to ${MAX_STATE}.`,
  competition: `Enter a competition of up to ${MAX_COMPETITION} characters.`,
  score: `Enter a whole number from 0 to ${MAX_SCORE}.`,
  status: "Choose draft or final.",
  notOnRoster: "This player is not on the roster.",
  twice: "This player is entered twice.",
  jersey: `Enter a jersey number of up to ${MAX_JERSEY} characters.`,
  position: `Enter a position of up to ${MAX_POSITION} characters.`,
  madeOverAttempted: "Made can't be more than attempted.",
  threesOverMade: "3-pointers made can't be more than field goals made.",
  threesOverAttempts: "3-point attempts can't be more than field goal attempts.",
  minutes: "Enter the minutes.",
  noLines: "Enter at least one player's stats.",
  notBasketball: "Game sheets are for basketball teams only.",
};

export const statMessage = (key) => `Enter a number from 0 to ${statMax(key)}.`;
export const pointsMessage = (expected) => `Points don't match the shots (expected ${expected}).`;
export const scoreWarning = (sum, score) => `Player points add up to ${sum}; the final score says ${score}.`;
export const seasonWarning = (gameSeason, teamSeason) =>
  `This game falls in the ${gameSeason} season; the team is set to ${teamSeason}.`;

// ---------------------------------------------------------------- Helpers
export const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const textOf = (value) => (value === null || value === undefined ? "" : String(value).trim());

const roundTo = (value, places) => {
  const f = 10 ** places;
  return Math.round(value * f) / f;
};

const isWhole = (value) => Math.abs(value - Math.round(value)) < 1e-6;

// "A", "A and B", "A, B and C".
export const joinNames = (names) =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// ------------------------------------------------------------------ Dates
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Today as "YYYY-MM-DD" in local time (the date field's value format).
export const localToday = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// A real "YYYY-MM-DD" day (no Feb 30).
export const isCalendarDate = (text) => {
  const m = DATE_RE.exec(String(text || ""));
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d;
};

const labelFrom = (start) => `${start}-${String((start + 1) % 100).padStart(2, "0")}`;

// "2026-09-22" -> "2026-27": the academic season (Aug 1 to Jul 31) a game
// date falls in, as profile_seasons.season_label does. Null for a non-date.
export const seasonLabel = (date) => {
  const m = DATE_RE.exec(String(date || "").slice(0, 10));
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return labelFrom(month >= 8 ? year : year - 1);
};

// A team's free-text season as a label: "2026-27" stays, "2026/2027"
// becomes "2026-27", a bare "2026" is the season ending that year
// ("2025-26"), as profile_seasons.normalize_season_label reads it.
export const normalizeSeason = (value) => {
  const text = textOf(value);
  const range = /^(\d{4})\s*[-/]\s*(\d{2}|\d{4})$/.exec(text);
  if (range) return labelFrom(Number(range[1]));
  if (/^\d{4}$/.test(text)) return labelFrom(Number(text) - 1);
  return null;
};

// ---------------------------------------------------------------- Numbers
const NUMBER_RE = /^(?:\d+(?:\.\d*)?|\.\d+)$/;
const CLOCK_RE = /^(\d{1,3}):([0-5]\d)$/;

// What a stat cell holds: {value, blank, error}. A blank cell is 0. Minutes
// keep one decimal (the server rounds 24.55 to 24.6 the same way) and take
// a clock reading ("24:30" is 24.5); everything else is a whole number. A
// value that does not parse, or is out of range, carries the server's
// message and a null value.
export const parseStat = (raw, key) => {
  const text = textOf(raw);
  if (!text) return { value: 0, blank: true, error: null };
  let value = null;
  const clock = key === "min" ? CLOCK_RE.exec(text) : null;
  if (clock) value = Number(clock[1]) + Number(clock[2]) / 60;
  else if (NUMBER_RE.test(text)) value = Number(text);
  if (value === null || !Number.isFinite(value)) return { value: null, blank: false, error: statMessage(key) };
  if (key === "min") value = roundTo(value, 1);
  if (value > statMax(key) || (key !== "min" && !isWhole(value))) return { value: null, blank: false, error: statMessage(key) };
  return { value, blank: false, error: null };
};

// A number from the server as cell text ("" for none).
export const numText = (value, key) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(key === "min" ? roundTo(n, 1) : n);
};

// "50.0%" from made and attempted; an en dash with no attempts, or when the
// numbers cannot be right (more made than attempted).
export const pctText = (made, att) => {
  if (made === null || made === undefined || att === null || att === undefined) return EMPTY;
  const m = Number(made);
  const a = Number(att);
  if (!Number.isFinite(m) || !Number.isFinite(a) || a <= 0 || m < 0 || m > a) return EMPTY;
  return `${((m / a) * 100).toFixed(1)}%`;
};

// ------------------------------------------------------------------- Rows
// An editor row: one roster player's line, as typed.
//   {playerId, name, jersey, position, dnp, cells: {min: "24", ...},
//    cerebro (the server's coverage check, or null), offRoster}
// `offRoster` marks a saved line whose player has left the roster since:
// the server keeps such a line editable on the game it belongs to.
export const emptyCells = () => Object.fromEntries(STAT_KEYS.map((k) => [k, ""]));

// The number a stat cell holds (0 when blank), or null when it does not parse.
export const cellValue = (row, key) => parseStat(row?.cells?.[key], key).value;

// A derived column's text for a row: "50.0%" or an en dash.
export const derivedText = (row, col) => pctText(cellValue(row, col.made), cellValue(row, col.att));

export const isCovered = (row) => (row?.cerebro?.status || null) === COVERED;

// A row the sheet shows but never edits: Cerebro already has the game.
export const isLocked = (row) => isCovered(row);

// A line is played when it is not marked DNP and has a minute or any stat
// above zero (team_games.line_played).
export const rowPlayed = (row) => Boolean(row && !row.dnp && STAT_KEYS.some((k) => (cellValue(row, k) || 0) > 0));

const POSITION_WORDS = {
  G: ["g", "guard", "pg", "sg", "point guard", "shooting guard", "combo guard"],
  F: ["f", "forward", "sf", "pf", "wing", "small forward", "power forward"],
  C: ["c", "center", "centre"],
};

// The sheet's G, F or C from a roster's free text ("Guard", "PG", "G/F" is
// a guard first); "" when it is none of them.
export const positionCode = (value) => {
  const text = textOf(value).toLowerCase();
  if (!text) return "";
  const hit = POSITIONS.find((code) => POSITION_WORDS[code].includes(text));
  if (hit) return hit;
  const first = text.split(/[/,-]/)[0].trim();
  return first && first !== text ? positionCode(first) : "";
};

const rosterRow = (player) => ({
  playerId: player.player_id,
  name: textOf(player.full_name),
  jersey: textOf(player.jersey_number),
  position: positionCode(player.position),
  dnp: false,
  cells: emptyCells(),
  cerebro: null,
  offRoster: false,
});

// One blank row per roster player, in roster order.
export const rowsFromRoster = (roster) => (roster || []).filter((p) => p && p.player_id).map(rosterRow);

// A row with a saved line in it. The game's jersey and position win over the
// roster's (they change from game to game).
const fillRow = (row, line) => ({
  ...row,
  jersey: typeof line.jersey === "string" || typeof line.jersey === "number" ? textOf(line.jersey) : row.jersey,
  position: line.position ? positionCode(line.position) || textOf(line.position) : row.position,
  dnp: Boolean(line.dnp),
  cells: line.dnp ? emptyCells() : Object.fromEntries(STAT_KEYS.map((k) => [k, numText(line[k], k)])),
  cerebro: line.cerebro || null,
});

// Change one cell of one row: jersey, position or a stat.
export const setRowCell = (rows, index, key, value) =>
  rows.map((row, i) => {
    if (i !== index) return row;
    if (key === "jersey" || key === "position") return { ...row, [key]: value };
    return { ...row, cells: { ...row.cells, [key]: value } };
  });

// Mark a row as did not play (its stats are cleared) or played again.
export const setRowDnp = (rows, index, dnp) =>
  rows.map((row, i) => (i === index ? { ...row, dnp: Boolean(dnp), cells: dnp ? emptyCells() : row.cells } : row));

// ------------------------------------------------------------------ Drafts
// The editor: {header, rows}. The header holds the game fields as typed.
export const emptyHeader = () => ({
  date: "",
  opponent: "",
  homeAway: null,
  city: "",
  state: "",
  competition: "",
  teamScore: "",
  opponentScore: "",
  status: STATUS_DRAFT,
});

// A new game: the roster, every line blank.
export const newDraft = (roster) => ({ header: emptyHeader(), rows: rowsFromRoster(roster) });

const headerFromGame = (game) => ({
  date: textOf(game.date).slice(0, 10),
  opponent: textOf(game.opponent),
  homeAway: HOME_AWAY.includes(game.home_away) ? game.home_away : null,
  city: textOf(game.location?.city),
  state: textOf(game.location?.state),
  competition: textOf(game.competition),
  teamScore: numText(game.team_score),
  opponentScore: numText(game.opponent_score),
  status: game.status === STATUS_FINAL ? STATUS_FINAL : STATUS_DRAFT,
});

// A saved game (the API's GAME) in the editor: every roster player in
// roster order with their saved line, then any saved line whose player has
// left the roster (`offRoster`).
export const draftFromGame = (game, roster) => {
  const g = game || {};
  const lines = new Map((g.lines || []).filter((l) => l && l.player_id).map((l) => [l.player_id, l]));
  const rows = rowsFromRoster(roster).map((row) => {
    const line = lines.get(row.playerId);
    lines.delete(row.playerId);
    return line ? fillRow(row, line) : row;
  });
  lines.forEach((line) => {
    rows.push(fillRow({ ...rosterRow({ player_id: line.player_id, full_name: line.name }), offRoster: true }, line));
  });
  return { header: headerFromGame(g), rows };
};

// What the editor holds, as text, so any edit (even one that does not parse
// yet) makes it differ from the game it opened with.
export const draftSnapshot = (draft) =>
  JSON.stringify({
    h: draft?.header || null,
    r: (draft?.rows || []).map((r) => [r.playerId, r.jersey, r.position, Boolean(r.dnp), STAT_KEYS.map((k) => r.cells?.[k] ?? "")]),
  });

const scoreValue = (text) => {
  const t = textOf(text);
  return t ? Number(t) : null;
};

const lineBody = (row) => ({
  player_id: row.playerId,
  jersey: textOf(row.jersey),
  position: textOf(row.position),
  dnp: Boolean(row.dnp),
  ...Object.fromEntries(STAT_KEYS.map((k) => [k, row.dnp ? 0 : cellValue(row, k) ?? 0])),
});

// A line that goes to the server: a played line, a DNP line, or one
// Cerebro already has (kept as saved). A blank line is left out.
export const rowSent = (row) => Boolean(row && (row.dnp || isCovered(row) || rowPlayed(row)));

// The POST / PUT body. A blank stat is 0. Lines go in sheet order, those of
// players who have left the roster since included (the server keeps them on
// the game they were saved with).
export const bodyFromDraft = (draft, status) => {
  const h = { ...emptyHeader(), ...(draft?.header || {}) };
  const city = textOf(h.city);
  const state = textOf(h.state);
  return {
    date: textOf(h.date),
    opponent: textOf(h.opponent),
    home_away: HOME_AWAY.includes(h.homeAway) ? h.homeAway : null,
    location: city || state ? { city, state } : null,
    competition: textOf(h.competition),
    team_score: scoreValue(h.teamScore),
    opponent_score: scoreValue(h.opponentScore),
    status: status || h.status || STATUS_DRAFT,
    lines: (draft?.rows || []).filter(rowSent).map(lineBody),
  };
};

// -------------------------------------------------------------- Totals
const zeroTotals = () => Object.fromEntries(STAT_KEYS.map((k) => [k, 0]));

// The sheet's totals row: every stat summed over the played lines, with how
// many players played.
export const lineTotals = (rows) => {
  const totals = zeroTotals();
  let players = 0;
  (rows || []).forEach((row) => {
    if (!row || !rowPlayed(row)) return;
    players += 1;
    STAT_KEYS.forEach((k) => {
      const v = cellValue(row, k);
      if (v) totals[k] += v;
    });
  });
  totals.min = roundTo(totals.min, 1);
  return { totals, players };
};

// What the players' points add up to (the score field's hint).
export const pointsSum = (rows) => lineTotals(rows).totals.pts;

// ----------------------------------------------------------- Validation
const checkScore = (text) => {
  const t = textOf(text);
  if (!t) return null;
  return /^\d+$/.test(t) && Number(t) <= MAX_SCORE ? null : MESSAGES.score;
};

// The line rules for one row: {errors: {key: message}, warning: message
// or null}. A points mismatch is an error on a final game and a warning on
// a draft; a played line of a final game needs minutes.
const checkRow = (row, final) => {
  const errors = {};
  const v = {};
  STAT_KEYS.forEach((k) => {
    const parsed = parseStat(row.cells?.[k], k);
    if (parsed.error) errors[k] = parsed.error;
    else v[k] = parsed.value;
  });
  const ok = (...keys) => keys.every((k) => !errors[k]);
  [["fgm", "fga"], ["tpm", "tpa"], ["ftm", "fta"]].forEach(([made, att]) => {
    if (ok(made, att) && v[made] > v[att]) errors[made] = MESSAGES.madeOverAttempted;
  });
  if (ok("tpm", "fgm") && v.tpm > v.fgm) errors.tpm = MESSAGES.threesOverMade;
  if (ok("tpa", "fga") && v.tpa > v.fga) errors.tpa = MESSAGES.threesOverAttempts;
  let warning = null;
  if (ok("pts", "fgm", "tpm", "ftm")) {
    const expected = 2 * v.fgm + v.tpm + v.ftm;
    if (v.pts !== expected) {
      if (final) errors.pts = pointsMessage(expected);
      else warning = pointsMessage(expected);
    }
  }
  const played = STAT_KEYS.some((k) => (v[k] || 0) > 0);
  if (final && played && !errors.min && !v.min) errors.min = MESSAGES.minutes;
  return { errors, warning };
};

// Everything wrong with a draft, the way the server would answer:
//   fields       {date|opponent|home_away|location|competition|team_score|
//                 opponent_score|status|lines: message}
//   lines        {player_id: {stat key: message}}
//   cellWarnings {player_id: {stat key: message}} (a draft's points mismatch)
//   lineWarnings [{playerId, key, message}] (the same, in roster order)
//   warnings     game-level notes that never block a save
//   ok           nothing blocks the save
// `status` overrides the draft's (the button the admin pressed); `team` is
// the API's team (its season); `today` is "YYYY-MM-DD".
export const validateDraft = (draft, { team = null, today = localToday(), status } = {}) => {
  const h = { ...emptyHeader(), ...(draft?.header || {}) };
  const rows = draft?.rows || [];
  const final = (status || h.status) === STATUS_FINAL;
  const fields = {};

  const date = textOf(h.date);
  if (!isCalendarDate(date) || date > today || date < FIRST_DATE) fields.date = MESSAGES.date;
  const opponent = textOf(h.opponent);
  if (!opponent || opponent.length > MAX_OPPONENT) fields.opponent = MESSAGES.opponent;
  if (h.homeAway !== null && h.homeAway !== undefined && h.homeAway !== "" && !HOME_AWAY.includes(h.homeAway)) {
    fields.home_away = MESSAGES.homeAway;
  }
  if (textOf(h.city).length > MAX_CITY || textOf(h.state).length > MAX_STATE) fields.location = MESSAGES.location;
  if (textOf(h.competition).length > MAX_COMPETITION) fields.competition = MESSAGES.competition;
  const teamScoreError = checkScore(h.teamScore);
  if (teamScoreError) fields.team_score = teamScoreError;
  const opponentScoreError = checkScore(h.opponentScore);
  if (opponentScoreError) fields.opponent_score = opponentScoreError;
  if (!STATUSES.includes(status || h.status)) fields.status = MESSAGES.status;

  const lines = {};
  const cellWarnings = {};
  const lineWarnings = [];
  const warnings = [];
  let played = 0;
  rows.forEach((row) => {
    if (!row) return;
    if (rowPlayed(row)) played += 1;
    // Cerebro's lines are read-only here.
    if (isCovered(row)) return;
    const errors = {};
    // The jersey and position of a line that is sent; the stats of every
    // line but a DNP one (a typo in a blank line is still a typo).
    if (rowSent(row)) {
      if (textOf(row.jersey).length > MAX_JERSEY) errors.jersey = MESSAGES.jersey;
      if (textOf(row.position).length > MAX_POSITION) errors.position = MESSAGES.position;
    }
    if (!row.dnp) {
      const checked = checkRow(row, final);
      Object.assign(errors, checked.errors);
      if (checked.warning) {
        cellWarnings[row.playerId] = { pts: checked.warning };
        lineWarnings.push({ playerId: row.playerId, key: "pts", message: checked.warning });
      }
    }
    if (Object.keys(errors).length) lines[row.playerId] = errors;
  });
  if (final && played === 0 && !Object.keys(lines).length) fields.lines = MESSAGES.noLines;

  const teamScore = checkScore(h.teamScore) || !textOf(h.teamScore) ? null : Number(textOf(h.teamScore));
  if (teamScore !== null) {
    const sum = pointsSum(rows);
    if (sum !== teamScore) warnings.push(scoreWarning(sum, teamScore));
  }
  const gameSeason = fields.date ? null : seasonLabel(date);
  const teamSeason = normalizeSeason(team?.season);
  if (gameSeason && teamSeason && gameSeason !== teamSeason) warnings.push(seasonWarning(gameSeason, teamSeason));

  const ok = !Object.keys(fields).length && !Object.keys(lines).length;
  return { fields, lines, cellWarnings, lineWarnings, warnings, ok };
};

// The server's 400 answer ({error: "invalid", message, fields, lines}) in
// the shape validateDraft returns: {fields, lines, message}.
export const errorsFromServer = (data) => {
  const d = data && typeof data === "object" ? data : {};
  const fields = {};
  if (d.fields && typeof d.fields === "object") {
    Object.entries(d.fields).forEach(([key, msg]) => {
      if (typeof msg === "string" && msg.trim()) fields[key] = msg.trim();
    });
  }
  const lines = {};
  if (d.lines && typeof d.lines === "object") {
    Object.entries(d.lines).forEach(([pid, cells]) => {
      if (!cells || typeof cells !== "object") return;
      Object.entries(cells).forEach(([key, msg]) => {
        if (typeof msg !== "string" || !msg.trim()) return;
        lines[pid] = { ...(lines[pid] || {}), [key]: msg.trim() };
      });
    });
  }
  return { fields, lines, message: typeof d.message === "string" ? d.message : "" };
};

// Errors to show: the server's answer under the sheet's own checks (a cell
// the admin has since changed shows its current problem, not the old one).
export const mergeErrors = (local, server) => {
  const fields = { ...(server?.fields || {}), ...(local?.fields || {}) };
  const lines = {};
  [server?.lines || {}, local?.lines || {}].forEach((source) => {
    Object.entries(source).forEach(([pid, cells]) => {
      lines[pid] = { ...(lines[pid] || {}), ...cells };
    });
  });
  return { fields, lines };
};

// Drop the server's error for one cell (the admin changed it).
export const clearServerCell = (server, playerId, key) => {
  if (!server?.lines?.[playerId] || !(key in server.lines[playerId])) return server;
  const rest = { ...server.lines[playerId] };
  delete rest[key];
  const lines = { ...server.lines };
  if (Object.keys(rest).length) lines[playerId] = rest;
  else delete lines[playerId];
  return { ...server, lines };
};

// --------------------------------------------------------- Season totals
const lineNum = (line, key) => {
  const n = Number(line?.[key]);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// A saved line that counts: a played line of a roster row that Cerebro
// does not already have (team_games.counted_lines, final games only).
export const lineCounted = (line) =>
  Boolean(
    line &&
      line.player_id &&
      !line.dnp &&
      (line.cerebro?.status || null) !== COVERED &&
      STAT_KEYS.some((k) => lineNum(line, k) > 0),
  );

// Per player over the season's final games: games played and every total,
// from the counted lines only (drafts, DNPs and Cerebro's games left out).
// Roster order first, then anyone who has left the roster by name.
export const seasonTotals = (games, roster) => {
  const order = new Map((roster || []).map((p, i) => [p.player_id, i]));
  const names = new Map((roster || []).map((p) => [p.player_id, textOf(p.full_name)]));
  const players = new Map();
  (games || []).forEach((game) => {
    if (game?.status !== STATUS_FINAL) return;
    (game.lines || []).forEach((line) => {
      if (!lineCounted(line)) return;
      const entry = players.get(line.player_id) || {
        playerId: line.player_id,
        name: names.get(line.player_id) || textOf(line.name),
        gp: 0,
        totals: zeroTotals(),
      };
      entry.gp += 1;
      STAT_KEYS.forEach((k) => {
        entry.totals[k] += lineNum(line, k);
      });
      players.set(line.player_id, entry);
    });
  });
  const rank = (id) => (order.has(id) ? order.get(id) : Number.MAX_SAFE_INTEGER);
  return [...players.values()]
    .map((entry) => ({ ...entry, totals: { ...entry.totals, min: roundTo(entry.totals.min, 1) } }))
    .sort((a, b) => rank(a.playerId) - rank(b.playerId) || a.name.localeCompare(b.name));
};

const perGame = (total, gp) => (gp > 0 ? (total / gp).toFixed(1) : EMPTY);

// The season table's columns.
export const SEASON_COLUMNS = [
  { key: "gp", label: "GP", name: "games played" },
  { key: "mpg", label: "MPG", name: "minutes per game" },
  { key: "ppg", label: "PPG", name: "points per game" },
  { key: "rpg", label: "RPG", name: "rebounds per game" },
  { key: "apg", label: "APG", name: "assists per game" },
  { key: "spg", label: "SPG", name: "steals per game" },
  { key: "bpg", label: "BPG", name: "blocks per game" },
  { key: "topg", label: "TOPG", name: "turnovers per game" },
  { key: "fgPct", label: "FG%", name: "field goal percentage" },
  { key: "tpPct", label: "3P%", name: "3-point percentage" },
  { key: "ftPct", label: "FT%", name: "free throw percentage" },
];

// One player's season line as text, keyed like SEASON_COLUMNS.
export const seasonLine = (entry) => {
  const t = entry?.totals || zeroTotals();
  const gp = entry?.gp || 0;
  return {
    gp: String(gp),
    mpg: perGame(t.min, gp),
    ppg: perGame(t.pts, gp),
    rpg: perGame(t.reb, gp),
    apg: perGame(t.ast, gp),
    spg: perGame(t.stl, gp),
    bpg: perGame(t.blk, gp),
    topg: perGame(t.tov, gp),
    fgPct: pctText(t.fgm, t.fga),
    tpPct: pctText(t.tpm, t.tpa),
    ftPct: pctText(t.ftm, t.fta),
  };
};

// ------------------------------------------------------------------ Paste
// Excel and Google Sheets copy cells as tab-separated rows. A paste of
// more than one cell fills the sheet:
//  - with a header row (Jayson's headers, any order, extra columns and the
//    percentage columns ignored), columns by header and rows by player
//    name; a header row with a Name column under the game's details (the
//    whole sheet copied) works the same, the rows above it left out;
//  - with the names in the first column and no header, the sheet's own
//    column order (Name, Jersey #, Position, MINUTES, ...);
//  - otherwise from the focused cell, right and down, the percentage
//    columns stepped over as they are in the sheet.
// Names match the roster exactly, then in any case, then by last name with
// a first name that starts the same ("Geo Gilpen" is Geovanni Gilpen); two
// candidates are never guessed between.

const unquote = (cell) => {
  const m = /^"([\s\S]*)"$/.exec(cell);
  return m ? m[1].replace(/""/g, '"') : cell;
};

// The pasted text as rows of trimmed cells (trailing blank lines dropped).
export const splitClipboard = (text) => {
  const lines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n");
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  return lines.map((line) => line.split("\t").map((cell) => unquote(cell.trim()).trim()));
};

// More than one cell: a tab or a line break inside the text.
export const isBlockPaste = (text) => /[\t\n\r]/.test(String(text ?? "").replace(/[\r\n]+$/, ""));

const HEADER_KEYS = {};
const alias = (key, names) =>
  names.forEach((n) => {
    HEADER_KEYS[n] = key;
  });
alias("name", ["name", "player", "players", "playername", "namefirstandlast", "fullname", "athlete"]);
alias("jersey", ["#", "jersey#", "jersey", "jerseynumber", "jerseyno", "no", "number", "num"]);
alias("position", ["pos", "position", "positiongfc"]);
alias("dnp", ["dnp"]);
alias("min", ["min", "mins", "minutes", "mp"]);
alias("pts", ["pts", "points"]);
alias("fgm", ["fgm", "fieldgoalsmade"]);
alias("fga", ["fga", "fieldgoalsattempted"]);
alias("tpm", ["3pm", "3fgm", "3ptm", "3pointersmade", "threepointersmade"]);
alias("tpa", ["3pa", "3fga", "3pta", "3pointersattempted", "threepointersattempted"]);
alias("ftm", ["ftm", "freethrowsmade"]);
alias("fta", ["fta", "freethrowsattempted"]);
alias("reb", ["reb", "rebs", "rebounds", "trb"]);
alias("ast", ["ast", "assists"]);
alias("stl", ["stl", "steals"]);
alias("blk", ["blk", "blocks"]);
alias("tov", ["to", "tov", "turnovers"]);
alias("pf", ["pf", "fouls", "personalfouls"]);
// The workbook's "FGM-FGA" style columns ("4-9").
alias("fgPair", ["fgmfga"]);
alias("tpPair", ["3pm3pa", "3fgm3fga", "3ptm3pta"]);
alias("ftPair", ["ftmfta"]);
// Worked out here, never read.
alias("skip", ["fg%", "fgpct", "3fg%", "3p%", "3pt%", "3ppct", "ft%", "ftpct"]);

const PAIRS = { fgPair: ["fgm", "fga"], tpPair: ["tpm", "tpa"], ftPair: ["ftm", "fta"] };

const normalizeHeader = (cell) => String(cell || "").toLowerCase().replace(/[^a-z0-9%#]/g, "");

// The sheet key of each pasted header cell (null for a column we ignore).
export const headerKeys = (cells) => (cells || []).map((cell) => HEADER_KEYS[normalizeHeader(cell)] || null);

// A header row names at least two of the sheet's columns and holds no
// numbers. "DNP" alone never makes one: a DNP player's line can read
// "DNP" in every stat cell.
export const isHeaderRow = (cells) => {
  const list = cells || [];
  if (list.some((cell) => NUMBER_RE.test(textOf(cell)))) return false;
  return new Set(headerKeys(list).filter((key) => key && key !== "dnp")).size >= 2;
};

// The sheet's columns from Jayson's Jersey # on (what a paste from the
// focused cell walks over), and the same with the name first.
const PASTE_ORDER = COLUMNS.filter((c) => c.kind !== "player").map((c) => (c.kind === "derived" ? "skip" : c.key));
const NAME_ORDER = ["name", ...PASTE_ORDER];

const SUFFIXES = new Set(["jr", "sr", "ii", "iii", "iv", "v"]);
const nameKey = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[.'’]/g, "")
    .replace(/[-\s]+/g, " ")
    .trim();
const nameParts = (name) => {
  const parts = nameKey(name).split(" ").filter(Boolean);
  while (parts.length > 1 && SUFFIXES.has(parts[parts.length - 1])) parts.pop();
  return { first: parts.length > 1 ? parts[0] : "", last: parts[parts.length - 1] || "" };
};

// Which roster row a pasted name is: {status: "matched", index} |
// {status: "ambiguous"} | {status: "unmatched"} | {status: "blank"}.
// `candidates` are [{index, name}].
export const matchPlayer = (name, candidates) => {
  const raw = String(name || "").replace(/\s+/g, " ").trim();
  if (!raw) return { status: "blank" };
  const pick = (hits) => {
    if (hits.length === 1) return { status: "matched", index: hits[0].index };
    return hits.length > 1 ? { status: "ambiguous" } : null;
  };
  const list = candidates || [];
  const exact = pick(list.filter((c) => String(c.name || "").replace(/\s+/g, " ").trim() === raw));
  if (exact) return exact;
  const key = nameKey(raw);
  const anyCase = pick(list.filter((c) => nameKey(c.name) === key));
  if (anyCase) return anyCase;
  const { first, last } = nameParts(raw);
  if (last) {
    const sameLast = list.filter((c) => nameParts(c.name).last === last);
    const starts = first
      ? sameLast.filter((c) => {
          const f = nameParts(c.name).first;
          return f && (f.startsWith(first) || first.startsWith(f));
        })
      : sameLast;
    const byLast = pick(starts);
    if (byLast) return byLast;
  }
  return { status: "unmatched" };
};

const DASHES = /^[-–—]+$/;
const PAIR_RE = /^(\d+)\s*[-/]\s*(\d+)$/;
const DNP_MARK = /^dnp\b/i;
const YES = /^(x|y|yes|true|dnp|1)$/i;
const TOTAL_ROW = /^(?:team\s*)?totals?$|^team$/i;

const cleanValue = (value) => {
  const t = textOf(value);
  return DASHES.test(t) ? "" : t;
};

// One pasted row as {sheet key: text}, through the column keys.
const recordFrom = (cells, keys) => {
  const record = {};
  keys.forEach((key, i) => {
    if (!key || key === "name" || key === "skip") return;
    const value = cells[i] ?? "";
    if (PAIRS[key]) {
      const [made, att] = PAIRS[key];
      const m = PAIR_RE.exec(textOf(value));
      if (m) {
        record[made] = m[1];
        record[att] = m[2];
      } else if (!cleanValue(value)) {
        record[made] = "";
        record[att] = "";
      } else {
        record[made] = textOf(value);
      }
      return;
    }
    if (key === "dnp") {
      if (YES.test(textOf(value))) record.dnp = true;
      return;
    }
    record[key] = value;
  });
  return record;
};

// A row with a pasted record in it: {row, filled (cells written)}. "DNP"
// in a stat cell (or a ticked DNP column) marks the player as did not play;
// numbers for a DNP player put them back in the game.
const applyRecord = (row, record) => {
  const next = { ...row, cells: { ...row.cells } };
  let filled = 0;
  if ("jersey" in record) {
    next.jersey = cleanValue(record.jersey);
    filled += 1;
  }
  if ("position" in record) {
    const p = cleanValue(record.position);
    next.position = positionCode(p) || p;
    filled += 1;
  }
  const dnp = record.dnp === true || STAT_KEYS.some((k) => k in record && DNP_MARK.test(textOf(record[k])));
  if (dnp) return { row: { ...next, dnp: true, cells: emptyCells() }, filled: filled + 1 };
  STAT_KEYS.forEach((k) => {
    if (!(k in record)) return;
    next.cells[k] = cleanValue(record[k]);
    filled += 1;
  });
  if (next.dnp && STAT_KEYS.some((k) => next.cells[k] !== "")) next.dnp = false;
  return { row: next, filled };
};

const newSummary = (mode) => ({ mode, players: 0, cells: 0, unmatched: [], ambiguous: [], skipped: [], overflow: false });

// Rows by player name.
const pasteByName = (rows, matrix, keys, nameCol, mode) => {
  const out = rows.slice();
  const summary = newSummary(mode);
  const touched = new Set();
  const candidates = rows.map((row, index) => ({ index, name: row.name }));
  matrix.forEach((cells) => {
    const name = textOf(cells[nameCol]);
    if (!name || TOTAL_ROW.test(name)) return;
    const hit = matchPlayer(name, candidates);
    if (hit.status === "ambiguous") {
      summary.ambiguous.push(name);
      return;
    }
    if (hit.status !== "matched") {
      summary.unmatched.push(name);
      return;
    }
    const row = out[hit.index];
    if (isLocked(row)) {
      if (!summary.skipped.includes(row.name)) summary.skipped.push(row.name);
      return;
    }
    const { row: next, filled } = applyRecord(row, recordFrom(cells, keys));
    out[hit.index] = next;
    summary.cells += filled;
    if (filled) touched.add(hit.index);
  });
  summary.players = touched.size;
  return { rows: out, summary };
};

// Rows one for one from `start` down (a paste from the focused cell, or a
// header without a name column).
const pasteFrom = (rows, matrix, keys, start, mode) => {
  const out = rows.slice();
  const summary = newSummary(mode);
  const touched = new Set();
  matrix.forEach((cells, i) => {
    const index = start + i;
    const hasValues = cells.some((c) => textOf(c) !== "");
    const row = out[index];
    if (!row) {
      if (hasValues) summary.overflow = true;
      return;
    }
    if (isCovered(row)) {
      if (hasValues && !summary.skipped.includes(row.name)) summary.skipped.push(row.name);
      return;
    }
    if (cells.some((c, j) => keys[j] === null && textOf(c) !== "")) summary.overflow = true;
    const { row: next, filled } = applyRecord(row, recordFrom(cells, keys));
    out[index] = next;
    summary.cells += filled;
    if (filled) touched.add(index);
  });
  summary.players = touched.size;
  return { rows: out, summary };
};

// No header, but the first column is names on the roster.
const startsWithNames = (matrix, rows) => {
  const firsts = matrix.map((cells) => textOf(cells[0])).filter(Boolean);
  if (!firsts.length || firsts.some((v) => NUMBER_RE.test(v) || DASHES.test(v))) return false;
  const candidates = rows.map((row, index) => ({ index, name: row.name }));
  return firsts.some((v) => matchPlayer(v, candidates).status !== "unmatched");
};

// Fill the rows from pasted text. `anchor` is the focused cell {row, key}.
// Returns {rows, summary: {mode "header"|"names"|"cells", players, cells,
// unmatched, ambiguous, skipped, overflow}}.
export const pasteIntoRows = (rows, text, anchor = {}) => {
  const list = rows || [];
  const matrix = splitClipboard(text);
  if (!matrix.length) return { rows: list, summary: newSummary("cells") };
  const startRow = Number.isInteger(anchor.row) && anchor.row >= 0 ? anchor.row : 0;
  if (isHeaderRow(matrix[0])) {
    const keys = headerKeys(matrix[0]);
    const nameCol = keys.indexOf("name");
    const body = matrix.slice(1);
    return nameCol >= 0 ? pasteByName(list, body, keys, nameCol, "header") : pasteFrom(list, body, keys, startRow, "header");
  }
  // The whole sheet copied: the game's details (Team Name, Game Date,
  // Opponent Name ...) above a header row with a Name column. The rows
  // above it are not players.
  const below = matrix.findIndex((cells) => isHeaderRow(cells) && headerKeys(cells).includes("name"));
  if (below > 0) {
    const keys = headerKeys(matrix[below]);
    return pasteByName(list, matrix.slice(below + 1), keys, keys.indexOf("name"), "header");
  }
  const width = Math.max(...matrix.map((cells) => cells.length));
  // Only the columns pasted: a shorter block (Name to PTS) leaves the rest
  // of each line as it was.
  if (startsWithNames(matrix, list)) return pasteByName(list, matrix, NAME_ORDER.slice(0, width), 0, "names");
  const startCol = Math.max(0, PASTE_ORDER.indexOf(anchor.key));
  const keys = Array.from({ length: width }, (_, j) => PASTE_ORDER[startCol + j] ?? null);
  return pasteFrom(list, matrix, keys, startRow, "cells");
};

// The line under the sheet after a paste.
export const pasteSummaryText = (summary) => {
  if (!summary) return "";
  const s = summary;
  const parts = [];
  if (s.mode === "cells") parts.push(s.cells ? `Filled ${plural(s.cells, "cell")}.` : "Nothing was filled.");
  else parts.push(s.players ? `Filled ${plural(s.players, "player")}.` : "Nothing was filled.");
  if (s.unmatched?.length) parts.push(`No roster match for ${joinNames(s.unmatched)}.`);
  if (s.ambiguous?.length) {
    parts.push(`More than one roster match for ${joinNames(s.ambiguous)}, so enter ${s.ambiguous.length === 1 ? "that line" : "those lines"} by hand.`);
  }
  if (s.skipped?.length) {
    parts.push(`${joinNames(s.skipped)} ${s.skipped.length === 1 ? "is" : "are"} already on Cerebro for this game, so ${s.skipped.length === 1 ? "that line was" : "those lines were"} left as is.`);
  }
  if (s.overflow) parts.push("Cells past the end of the sheet were left out.");
  return parts.join(" ");
};

// ---------------------------------------------------------- Score update
// The background score update after a save (REFRESH from the API).
export const REFRESH_BUSY_STATES = ["queued", "running"];
export const refreshBusy = (refresh) => REFRESH_BUSY_STATES.includes(refresh?.state);

// {text, tone: "busy"|"done"|"failed"|"idle", busy, detail}. The detail
// is the server's own word on the run ("Checking which games Cerebro
// already has."), else how far it got.
export const describeRefresh = (refresh) => {
  const r = refresh || {};
  const athletes = Number(r.athletes) || 0;
  const done = Number(r.done) || 0;
  const errors = Number(r.errors) || 0;
  const message = textOf(r.message);
  if (refreshBusy(r)) {
    return {
      tone: "busy",
      busy: true,
      text: athletes > 0 ? `Updating scores for ${plural(athletes, "player")}. This can take a few minutes.` : "Updating scores. This can take a few minutes.",
      detail: message || (athletes > 0 && done > 0 ? `${done} of ${athletes} done` : ""),
    };
  }
  if (r.state === "done") {
    return {
      tone: "done",
      busy: false,
      text: "Scores are up to date.",
      detail: message || (errors > 0 ? `${plural(errors, "player")} could not be updated.` : ""),
    };
  }
  if (r.state === "failed") {
    // The server's own "... Run it again." says what the line above says;
    // a cause of its own ("The team was deleted.") is worth showing.
    const detail = /run it again\.$/i.test(message) ? "" : message;
    return { tone: "failed", busy: false, text: "The last score update stopped. Try again.", detail };
  }
  return { tone: "idle", busy: false, text: "Scores update in the background after each save.", detail: message };
};

// ------------------------------------------------------------- Game list
// "W", "L" or "T" from the final score; null until both scores are in.
export const resultOf = (game) => {
  const us = game?.team_score;
  const them = game?.opponent_score;
  if (us === null || us === undefined || us === "" || them === null || them === undefined || them === "") return null;
  const a = Number(us);
  const b = Number(them);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return a > b ? "W" : a < b ? "L" : "T";
};

// "72-65", or an en dash until both scores are in.
export const scoreText = (game) => (resultOf(game) ? `${Number(game.team_score)}-${Number(game.opponent_score)}` : EMPTY);

const HOME_AWAY_LABELS = { home: "Home", away: "Away", neutral: "Neutral" };
export const homeAwayLabel = (value) => HOME_AWAY_LABELS[value] || EMPTY;

// "Sep 22, 2026".
export const gameDateText = (date) => fmtDate(textOf(date).slice(0, 10)) || EMPTY;

// "10", or "10 · 2 on Cerebro".
export const playersText = (game) => {
  const players = Number(game?.players) || 0;
  const covered = Number(game?.covered) || 0;
  return covered ? `${players} · ${covered} on Cerebro` : String(players);
};

// The server's times are UTC written without an offset
// ("2026-10-04T02:30:00"), which a browser would read as local time: an
// evening edit in Los Angeles would show the next day.
const utcTime = (value) =>
  typeof value === "string" && /T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value) ? `${value}Z` : value;

// "Oct 3, 2026 · jayson@cache.ai".
export const editedText = (game) => {
  const when = utcTime(game?.updated_at || game?.created_at);
  const who = textOf(game?.updated_by || game?.created_by);
  const parts = [when ? fmtDate(when) : "", who].filter(Boolean);
  return parts.length ? parts.join(" · ") : EMPTY;
};

const teamNameOf = (team) => textOf(team?.name) || textOf(team?.organization);

// "Sierra Canyon School vs. Bella Vista".
export const gameTitle = (team, game) => {
  const name = teamNameOf(team);
  const opponent = textOf(game?.opponent);
  if (name && opponent) return `${name} vs. ${opponent}`;
  return opponent || name || "Game";
};

export const savedMessage = (game) => (textOf(game?.opponent) ? `Saved the game against ${textOf(game.opponent)}.` : "Saved the game.");
export const deletedMessage = (game) => (textOf(game?.opponent) ? `Deleted the game against ${textOf(game.opponent)}.` : "Deleted the game.");

// Only basketball has a sheet so far (the server reads "bb" as basketball too).
export const isBasketball = (team) => !team?.sport || ["basketball", "bb"].includes(textOf(team.sport).toLowerCase());
